import React, { useState, useEffect, useRef } from "react";
import { Card, CardColor, CardValue } from "../types/game";

interface GameLog {
  id: string;
  timestamp: Date;
  message: string;
}

interface LogModalProps {
  logs: GameLog[];
  onClose: () => void;
}

export function LogModal({ logs, onClose }: LogModalProps) {
  const [closeHovered, setCloseHovered] = useState(false);
  const [footerButtonHovered, setFooterButtonHovered] = useState(false);
  const [hoveredLogId, setHoveredLogId] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lastLogRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (lastLogRef.current) {
      lastLogRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [logs.length]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getLogIcon = (message: string): string => {
    if (message.includes("bị phạt")) return "⚠️";
    if (message.includes("đã đánh")) return "🃏";
    if (message.includes("đã rút")) return "📥";
    if (message.includes("đã gọi UNO")) return "🔄";
    if (message.includes("Đến lượt")) return "⏭️";
    if (message.includes("bắt đầu")) return "🎮";
    if (message.includes("thắng")) return "🎉";
    return "📝";
  };

  const getCardColorFromMessage = (message: string): string | null => {
    // Extract color from message - check for Vietnamese color names first
    if (message.includes("Đỏ") || message.match(/chọn màu\s+red/i)) return "#E53935";
    if (message.includes("Xanh dương") || message.match(/chọn màu\s+blue/i)) return "#1E88E5";
    if (message.includes("Xanh lá") || message.match(/chọn màu\s+green/i)) return "#43A047";
    if (message.includes("Vàng") || message.match(/chọn màu\s+yellow/i)) return "#FDD835";
    
    // Also check for English color names in the message
    if (message.match(/\bred\b/i)) return "#E53935";
    if (message.match(/\bblue\b/i)) return "#1E88E5";
    if (message.match(/\bgreen\b/i)) return "#43A047";
    if (message.match(/\byellow\b/i)) return "#FDD835";
    
    return null;
  };

  const getLogColor = (message: string): string => {
    // If it's a penalty, use orange/red color
    if (message.includes("bị phạt")) return "#f56565";
    
    // If it's a card play message, use card color
    const cardColor = getCardColorFromMessage(message);
    if (cardColor) return cardColor;
    
    // If it's UNO call, use purple color
    if (message.includes("đã gọi UNO") || message.includes("UNO")) return "#9f7aea";
    
    // Otherwise use default colors by action type
    if (message.includes("đã đánh")) return "#667eea";
    if (message.includes("đã rút")) return "#48bb78";
    if (message.includes("Đến lượt")) return "#9f7aea";
    if (message.includes("bắt đầu")) return "#4299e1";
    if (message.includes("thắng")) return "#f56565";
    return "#718096";
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <h2 style={styles.title}>📋 Nhật Ký Trò Chơi</h2>
            {logs.length > 0 && (
              <span style={styles.logCount}>{logs.length} sự kiện</span>
            )}
          </div>
          <button 
            style={{
              ...styles.closeButton,
              ...(closeHovered ? styles.closeButtonHovered : {}),
            }}
            onClick={onClose}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
          >
            ✕
          </button>
        </div>
        
        <div ref={contentRef} style={styles.content}>
          {logs.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📝</div>
              <p style={styles.emptyText}>Chưa có nhật ký nào</p>
              <p style={styles.emptySubtext}>Các hành động trong trò chơi sẽ được ghi lại ở đây</p>
            </div>
          ) : (
            <div style={styles.logList}>
              {logs.map((log, index) => {
                const isLast = index === logs.length - 1;
                const icon = getLogIcon(log.message);
                const borderColor = getLogColor(log.message);
                const cardColor = getCardColorFromMessage(log.message);
                
                // Highlight color in message if it's a card play, UNO call, or penalty
                const renderMessage = () => {
                  const isPenalty = log.message.includes("bị phạt");
                  const isUNOCall = log.message.includes("đã gọi UNO") || log.message.includes("UNO");
                  
                  // Handle penalty - highlight "bị phạt" in red
                  if (isPenalty) {
                    const parts = log.message.split(/(bị phạt)/i);
                    return (
                      <span style={styles.message}>
                        {parts.map((part, i) => {
                          if (/^bị phạt$/i.test(part.trim())) {
                            return (
                              <span key={i} style={{ ...styles.colorHighlight, color: "#f56565" }}>
                                {part}
                              </span>
                            );
                          }
                          return <span key={i}>{part}</span>;
                        })}
                      </span>
                    );
                  }
                  
                  // Handle UNO call - highlight UNO in purple
                  if (isUNOCall) {
                    const parts = log.message.split(/(UNO)/i);
                    return (
                      <span style={styles.message}>
                        {parts.map((part, i) => {
                          if (/^UNO$/i.test(part.trim())) {
                            return (
                              <span key={i} style={{ ...styles.colorHighlight, color: "#9f7aea" }}>
                                {part}
                              </span>
                            );
                          }
                          return <span key={i}>{part}</span>;
                        })}
                      </span>
                    );
                  }
                  
                  // Handle card play - highlight color parts
                  if (cardColor && log.message.includes("đã đánh")) {
                    const colorRegex = /(Đỏ|Xanh dương|Xanh lá|Vàng|red|blue|green|yellow)/i;
                    const parts = log.message.split(colorRegex);
                    return (
                      <span style={styles.message}>
                        {parts.map((part, i) => {
                          const isColor = /^(Đỏ|Xanh dương|Xanh lá|Vàng|red|blue|green|yellow)$/i.test(part.trim());
                          if (isColor) {
                            // Get the actual color for this specific part
                            let partColor = cardColor;
                            if (/^(Đỏ|red)$/i.test(part.trim())) partColor = "#E53935";
                            else if (/^(Xanh dương|blue)$/i.test(part.trim())) partColor = "#1E88E5";
                            else if (/^(Xanh lá|green)$/i.test(part.trim())) partColor = "#43A047";
                            else if (/^(Vàng|yellow)$/i.test(part.trim())) partColor = "#FDD835";
                            
                            return (
                              <span key={i} style={{ ...styles.colorHighlight, color: partColor }}>
                                {part}
                              </span>
                            );
                          }
                          return <span key={i}>{part}</span>;
                        })}
                      </span>
                    );
                  }
                  
                  return <span style={styles.message}>{log.message}</span>;
                };
                
                return (
                  <div 
                    key={log.id} 
                    ref={isLast ? lastLogRef : null}
                    onMouseEnter={() => setHoveredLogId(log.id)}
                    onMouseLeave={() => setHoveredLogId(null)}
                    style={{
                      ...styles.logItem,
                      borderLeftColor: borderColor,
                      ...(hoveredLogId === log.id ? styles.logItemHovered : {}),
                    }}
                  >
                    <span style={styles.logIcon}>{icon}</span>
                    <span style={styles.timestamp}>
                      {log.timestamp.toLocaleTimeString("vi-VN", { 
                        hour: "2-digit", 
                        minute: "2-digit",
                        second: "2-digit"
                      })}
                    </span>
                    {renderMessage()}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button 
            style={{
              ...styles.closeFooterButton,
              ...(footerButtonHovered ? styles.closeFooterButtonHover : {}),
            }}
            onClick={onClose}
            onMouseEnter={() => setFooterButtonHovered(true)}
            onMouseLeave={() => setFooterButtonHovered(false)}
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    background: "rgba(30, 30, 50, 0.98)",
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    maxWidth: "600px",
    width: "100%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.1)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 30px",
    borderBottom: "2px solid rgba(255,255,255,0.1)",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  headerContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  title: {
    margin: 0,
    color: "white",
    fontSize: "24px",
    fontWeight: "700",
  },
  logCount: {
    color: "rgba(255,255,255,0.9)",
    fontSize: "13px",
    fontWeight: "500",
  },
  closeButton: {
    background: "rgba(255,255,255,0.2)",
    border: "none",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    color: "white",
    fontSize: "20px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },
  closeButtonHovered: {
    background: "rgba(255,255,255,0.3)",
    transform: "scale(1.1)",
  },
  content: {
    padding: "20px 24px",
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
    background: "rgba(20, 20, 30, 0.5)",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "16px",
    opacity: 0.5,
  },
  emptyText: {
    color: "rgba(255,255,255,0.9)",
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "8px",
  },
  emptySubtext: {
    color: "rgba(255,255,255,0.6)",
    fontSize: "14px",
  },
  logList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  logItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    background: "rgba(40, 40, 60, 0.6)",
    borderRadius: "10px",
    borderLeft: "4px solid #667eea",
    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
    transition: "all 0.2s ease",
    cursor: "default",
  },
  logItemHovered: {
    transform: "translateX(4px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  logIcon: {
    fontSize: "18px",
    flexShrink: 0,
  },
  timestamp: {
    fontSize: "11px",
    color: "rgba(255,255,255,0.5)",
    fontWeight: "600",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    flexShrink: 0,
    minWidth: "70px",
  },
  message: {
    fontSize: "15px",
    color: "rgba(255,255,255,0.9)",
    lineHeight: "1.5",
    fontWeight: "500",
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  colorHighlight: {
    fontWeight: "700",
    textShadow: "0 0 2px currentColor",
  },
  footer: {
    padding: "20px 30px",
    borderTop: "2px solid rgba(255,255,255,0.1)",
    display: "flex",
    justifyContent: "flex-end",
    background: "rgba(20, 20, 30, 0.5)",
  },
  closeFooterButton: {
    padding: "10px 24px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
  },
  closeFooterButtonHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
  },
};
